import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import ConfirmDialog from "components/ConfirmDialog";
import TierBadge from "components/Companies/TierBadge";
import { StatusBadge } from "components/Account/StatusBadge";
import { Network, Unlink, Building2, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Internal-only Child Companies card on the Company Detail Overview tab.
 * Symmetric counterpart to ParentCompanyCard — lists children of this
 * (parent) company with per-row navigation and an Unlink action that
 * clears the child's parentCompanyId.
 *
 * Render gating is handled by the parent (OverviewTab): only renders when
 * the company is a potential parent (companyType === "client" and has no
 * parentCompanyId itself — 2-level depth rule). This component additionally
 * hides itself when the fetched children list is empty.
 */
export default function ChildCompaniesCard({ company, refetch }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null); // child object or null
  const [unlinking, setUnlinking] = useState(false);

  const fetchChildren = useCallback(async () => {
    if (!company?.companyId || !auth?.user || !effectiveCompanyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getCompanyChildren(company.companyId),
        auth.user,
        effectiveCompanyId
      );
      setChildren(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error("[ChildCompaniesCard] Fetch children failed", err);
      setError(err.response?.data?.error || "Failed to load child companies");
      setChildren([]);
    } finally {
      setLoading(false);
    }
  }, [company?.companyId, auth?.user, effectiveCompanyId]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const handleUnlink = async () => {
    if (!confirmUnlink) return;
    setUnlinking(true);
    try {
      await makeApiRequest(
        "put",
        apiRoutes.updateCompany(confirmUnlink.companyId),
        auth.user,
        effectiveCompanyId,
        { parentCompanyId: null }
      );
      toast({
        status: "success",
        title: "Child unlinked",
        description: `${confirmUnlink.name} is no longer a child of ${company.name}.`,
      });
      setConfirmUnlink(null);
      fetchChildren();
      if (typeof refetch === "function") refetch();
    } catch (err) {
      console.error("[ChildCompaniesCard] Unlink failed", err);
      toast({
        status: "destructive",
        title: "Failed to unlink child",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setUnlinking(false);
    }
  };

  // Hide entirely when there are no children and we're not loading/errored.
  // Per spec: no empty state — don't render the card at all.
  if (!loading && !error && children.length === 0) {
    return null;
  }

  return (
    <div className="surface">
      <div className="px-6 py-4 border-b border-border flex items-center space-x-3">
        <div className="p-2 bg-brand/20 rounded-lg">
          <Network className="w-5 h-5 text-brand" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">
          Child Companies
        </h2>
      </div>
      <div className="p-6 space-y-3">
        <p className="text-sm text-muted-foreground">
          Companies linked to {company.name} as children. Users of this company
          can switch into any child to view its data.
        </p>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : error ? (
          <div className="text-sm text-destructive">{error}</div>
        ) : (
          <ul className="space-y-2">
            {children.map((child) => (
              <li
                key={child.companyId}
                className="flex items-center justify-between gap-3 p-4 rounded-lg border border-border bg-muted/40"
              >
                <button
                  type="button"
                  className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
                  onClick={() => navigate(`/companies/${child.companyId}`)}
                  title={`View ${child.name}`}
                >
                  <div className="p-2 rounded-md bg-brand/20 shrink-0">
                    <Building2 className="h-4 w-4 text-brand" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {child.name || child.companyId}
                    </div>
                    {child.slug && (
                      <div className="text-xs text-muted-foreground truncate font-mono">
                        {child.slug}
                      </div>
                    )}
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {/* SOUND-795: tier shown so a parent's cascade is visible here */}
                  {child.tier && (
                    <TierBadge tier={child.tier} />
                  )}
                  {child.status && <StatusBadge status={child.status} />}
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigate(`/companies/${child.companyId}`)}
                    title="View child"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => setConfirmUnlink(child)}
                  >
                    <Unlink className="h-4 w-4" />
                    Unlink
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        open={!!confirmUnlink}
        title="Unlink child company?"
        description={
          confirmUnlink
            ? `${confirmUnlink.name} will no longer be a child of ${company.name}. ${company.name}'s users will lose access to ${confirmUnlink.name}'s data.`
            : ""
        }
        confirmLabel={unlinking ? "Unlinking..." : "Unlink"}
        variant="destructive"
        onConfirm={handleUnlink}
        onCancel={() => !unlinking && setConfirmUnlink(null)}
      />
    </div>
  );
}
